const MOTION = Object.freeze({
  navigation: Object.freeze({ duration: 500, easing: 'cubic-bezier(.6,0,.4,1)' }),
  default: Object.freeze({ duration: 500, easing: 'cubic-bezier(.3,1,.3,1)' }),
  fast: Object.freeze({ duration: 300, easing: 'cubic-bezier(.7,0,.3,1)' }),
  image: Object.freeze({ duration: 1000, easing: 'cubic-bezier(.3,1,.3,1)' }),
  slideshowAutoplay: 6000,
  marquee: 12000,
});

function normalizeIndex(index, length) {
  if (!Number.isFinite(index) || !Number.isInteger(length) || length <= 0) return 0;
  return ((index % length) + length) % length;
}

function nextTabIndex(current, key, length) {
  const index = normalizeIndex(current, length);

  if (key === 'Home') return 0;
  if (key === 'End') return Math.max(0, length - 1);
  if (key === 'ArrowRight' || key === 'ArrowDown') return normalizeIndex(index + 1, length);
  if (key === 'ArrowLeft' || key === 'ArrowUp') return normalizeIndex(index - 1, length);

  return index;
}

function nextAutoplayIndex(current, length) {
  return normalizeIndex(current + 1, length);
}

function resolveDragOffset(distance, slideSize) {
  const safeDistance = Number.isFinite(distance) ? distance : 0;
  const safeSize = Math.max(0, Number.isFinite(slideSize) ? slideSize : 0);
  if (!safeSize) return safeDistance;
  return Math.max(-safeSize, Math.min(safeSize, safeDistance));
}

function hasPointerDragStarted(distance, threshold = 6) {
  const safeDistance = Number.isFinite(distance) ? Math.abs(distance) : 0;
  const safeThreshold = Math.max(0, Number.isFinite(threshold) ? threshold : 6);
  return safeDistance > safeThreshold;
}

function resolveDragReleaseIndex(
  current,
  { distance = 0, velocity = 0, duration = 301, slideSize = 0, length = 0 } = {},
) {
  const safeDistance = Number.isFinite(distance) ? distance : 0;
  const safeVelocity = Number.isFinite(velocity) ? velocity : 0;
  const shortSwipe = Number.isFinite(duration) && duration <= 300;
  const threshold = shortSwipe ? 2 : Math.max(2, Math.max(0, slideSize) * 0.5);
  const shouldAdvance = Math.abs(safeDistance) >= threshold || Math.abs(safeVelocity) >= 0.4;
  if (!shouldAdvance) return normalizeIndex(current, length);
  return normalizeIndex(current + (safeDistance < 0 || (safeDistance === 0 && safeVelocity < 0) ? 1 : -1), length);
}

function resolveRailSnapIndex({ startScroll = 0, currentScroll = 0, velocity = 0, step = 0 } = {}) {
  const safeStep = Math.max(0, Number.isFinite(step) ? step : 0);
  if (!safeStep) return 0;
  const safeStart = Number.isFinite(startScroll) ? startScroll : 0;
  const safeCurrent = Number.isFinite(currentScroll) ? currentScroll : safeStart;
  const safeVelocity = Number.isFinite(velocity) ? velocity : 0;
  const startIndex = Math.round(safeStart / safeStep);
  const projectedScroll = safeCurrent - safeVelocity * 220;
  let targetIndex = Math.round(projectedScroll / safeStep);
  if (safeVelocity <= -0.4) targetIndex = Math.max(targetIndex, startIndex + 1);
  if (safeVelocity >= 0.4) targetIndex = Math.min(targetIndex, startIndex - 1);
  return Math.max(startIndex - 2, Math.min(startIndex + 2, targetIndex));
}

function resolveHeaderState(previousY, currentY, threshold = 12) {
  const safeThreshold = Math.max(0, threshold);
  if (currentY <= safeThreshold) return 'top';
  return currentY === previousY ? 'idle' : 'pinned';
}

function updateBodyLockCount(count, action) {
  const safeCount = Math.max(0, Number.isFinite(count) ? Math.trunc(count) : 0);
  if (action === 'acquire') return safeCount + 1;
  if (action === 'release') return Math.max(0, safeCount - 1);
  return safeCount;
}

function buildQuickViewItem(variantId, quantity) {
  const id = Number(variantId);
  if (!Number.isFinite(id) || id <= 0) return null;
  return {
    id,
    quantity: Math.max(1, Number.parseInt(quantity, 10) || 1),
  };
}

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
let bodyLockCount = 0;

function animate(element, keyframes, options) {
  if (!element || reducedMotion.matches || typeof element.animate !== 'function') return null;
  return element.animate(keyframes, options);
}

function setBodyLock(action) {
  bodyLockCount = updateBodyLockCount(bodyLockCount, action);
  document.documentElement.toggleAttribute('data-sleek-lock', bodyLockCount > 0);
}

function acquireOwnedBodyLock(owner) {
  if (owner.ownsBodyLock) return;
  owner.ownsBodyLock = true;
  setBodyLock('acquire');
}

function releaseOwnedBodyLock(owner) {
  if (!owner.ownsBodyLock) return;
  owner.ownsBodyLock = false;
  setBodyLock('release');
}

class SleekMotion extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekMotionReady === 'true') return;
    this.dataset.sleekMotionReady = 'true';

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      this.dataset.sleekMotionState = 'visible';
      return;
    }

    this.dataset.sleekMotionState = 'pending';
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        this.dataset.sleekMotionState = 'visible';
        this.observer?.disconnect();
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

class SleekTabs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekTabsReady === 'true') return;
    this.dataset.sleekTabsReady = 'true';
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.select = this.querySelector('[data-sleek-tab-select]');
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.activate(index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });
    this.select?.addEventListener('change', () => this.activate(this.select.selectedIndex));
  }

  onKeydown(event, current) {
    if (['Enter', ' '].includes(event.key)) this.activate(current, true);
    if (['Enter', ' '].includes(event.key)) {
      event.preventDefault();
      return;
    }
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const index = nextTabIndex(current, event.key, this.tabs.length);
    this.tabs.forEach((tab, tabIndex) => { tab.tabIndex = tabIndex === index ? 0 : -1; });
    this.tabs[index]?.focus();
  }

  activate(requestedIndex, moveFocus = false) {
    const index = normalizeIndex(requestedIndex, this.tabs.length);
    const currentIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    if (index === currentIndex) {
      if (moveFocus) this.tabs[index]?.focus();
      return;
    }

    const outgoing = this.panels.find((panel) => !panel.hidden);
    const incoming = this.panels[index];
    const activationToken = Symbol('tab-activation');
    this.activationToken = activationToken;
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    if (this.select) this.select.selectedIndex = index;

    const revealIncoming = () => {
      if (this.activationToken !== activationToken) return;
      if (outgoing) outgoing.hidden = true;
      if (incoming) {
        incoming.hidden = false;
        const incomingAnimation = animate(
          incoming,
          [
            { opacity: 0, transform: 'translateY(2rem)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: MOTION.fast.duration, easing: MOTION.fast.easing },
        );
        const revealItems = () => {
          incoming.querySelectorAll('[data-sleek-product-card], .sleek-social-card').forEach((item, itemIndex) => {
            animate(
              item,
              [
                { opacity: 0.01, transform: 'translateY(3.5rem)' },
                { opacity: 1, transform: 'translateY(0)' },
              ],
              { duration: MOTION.default.duration, delay: itemIndex * 100, easing: 'cubic-bezier(0,0,.3,1)' },
            );
          });
        };
        if (incomingAnimation) incomingAnimation.addEventListener('finish', revealItems, { once: true });
        else revealItems();
      }
    };

    if (outgoing) {
      const outgoingAnimation = animate(
        outgoing,
        [
          { opacity: 1, transform: 'translateY(0)' },
          { opacity: 0, transform: 'translateY(2rem)' },
        ],
        { duration: MOTION.fast.duration, easing: MOTION.fast.easing },
      );
      if (outgoingAnimation) outgoingAnimation.addEventListener('finish', revealIncoming, { once: true });
      else revealIncoming();
    } else revealIncoming();

    if (moveFocus) this.tabs[index]?.focus();
    if (this.classList.contains('sleek-social-products__layout')) {
      const section = this.closest('.sleek-social-products');
      if (section && section.getBoundingClientRect().top < 0) section.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    }
    this.dispatchEvent(new CustomEvent('sleek:tab-change', { bubbles: true, detail: { index } }));
  }
}

class SleekSlider extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekSliderReady === 'true') return;
    this.dataset.sleekSliderReady = 'true';
    this.slides = [...this.querySelectorAll('[data-sleek-slide]')];
    this.dots = [...this.querySelectorAll('[data-sleek-slide-to]')];
    this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
    this.paused = false;

    this.querySelector('[data-sleek-prev]')?.addEventListener('click', () => this.show(this.index - 1, 'previous'));
    this.querySelector('[data-sleek-next]')?.addEventListener('click', () => this.show(this.index + 1, 'next'));
    this.querySelector('[data-sleek-pause]')?.addEventListener('click', () => this.togglePause());
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.sleekSlideTo), 'direct')));
    this.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.addEventListener('pointermove', (event) => this.onPointerMove(event));
    this.addEventListener('pointerup', (event) => this.finishPointer(event));
    this.addEventListener('pointercancel', (event) => this.finishPointer(event, true));
    this.addEventListener('lostpointercapture', (event) => this.finishPointer(event, true, true));
    this.addEventListener('dragstart', (event) => event.preventDefault());
    this.addEventListener('click', (event) => {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }, true);
    this.addEventListener('mouseenter', () => this.stopTimer());
    this.addEventListener('mouseleave', () => this.startTimer());
    this.addEventListener('focusin', () => this.stopTimer());
    this.addEventListener('focusout', () => this.startTimer());
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stopTimer() : this.startTimer()));
    this.sync();
    this.startTimer();
  }

  disconnectedCallback() {
    this.stopTimer();
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target?.closest?.('[data-sleek-prev], [data-sleek-next], [data-sleek-pause], [data-sleek-slide-to]')) return;
    this.pointerId = event.pointerId;
    this.pointerStart = event.clientX;
    this.pointerStartY = event.clientY ?? 0;
    this.pointerLast = event.clientX;
    this.pointerLastTime = event.timeStamp;
    this.pointerVelocity = 0;
    this.pointerRejected = false;
    this.pointerCaptured = false;
    this.suppressClick = false;
    this.stopTimer();
    this.style.setProperty('--sleek-discrete-drag-offset', '0px');
  }

  onPointerMove(event) {
    if (!Number.isFinite(this.pointerStart) || event.pointerId !== this.pointerId) return;
    const distance = event.clientX - this.pointerStart;
    const verticalDistance = (event.clientY ?? this.pointerStartY) - this.pointerStartY;
    if (!this.pointerRejected && Math.abs(verticalDistance) > Math.abs(distance) && Math.abs(verticalDistance) > 8) {
      this.pointerRejected = true;
      this.toggleAttribute('data-dragging', false);
      this.style.setProperty('--sleek-discrete-drag-offset', '0px');
    }
    if (this.pointerRejected) return;
    const elapsed = Math.max(1, event.timeStamp - this.pointerLastTime);
    this.pointerVelocity = (event.clientX - this.pointerLast) / elapsed;
    this.pointerLast = event.clientX;
    this.pointerLastTime = event.timeStamp;
    const limit = Math.max(64, (this.clientWidth || 320) * 0.34);
    const offset = Math.max(-limit, Math.min(limit, distance));
    if (Math.abs(distance) > 6 && !this.pointerCaptured) {
      this.pointerCaptured = true;
      this.setPointerCapture?.(event.pointerId);
    }
    this.toggleAttribute('data-dragging', Math.abs(distance) > 6);
    this.style.setProperty('--sleek-discrete-drag-offset', `${offset}px`);
    if (Math.abs(distance) > 6) this.suppressClick = true;
  }

  finishPointer(event, cancelled = false, captureLost = false) {
    if (!Number.isFinite(this.pointerStart) || event.pointerId !== this.pointerId) return;
    const distance = cancelled || this.pointerRejected ? 0 : event.clientX - this.pointerStart;
    const threshold = Math.max(48, (this.clientWidth || 320) * 0.16);
    const shouldAdvance = Math.abs(distance) >= threshold || Math.abs(this.pointerVelocity) >= 0.45;
    const pointerCaptured = this.pointerCaptured;
    this.pointerStart = null;
    this.pointerStartY = null;
    this.pointerId = null;
    this.pointerVelocity = 0;
    this.pointerRejected = false;
    this.pointerCaptured = false;
    if (!captureLost && pointerCaptured && this.hasPointerCapture?.(event.pointerId)) {
      this.releasePointerCapture?.(event.pointerId);
    }
    this.toggleAttribute('data-dragging', false);
    this.style.setProperty('--sleek-discrete-drag-offset', '0px');
    if (shouldAdvance && distance !== 0) {
      this.show(this.index + (distance < 0 ? 1 : -1), distance < 0 ? 'next' : 'previous');
    } else {
      this.startTimer();
    }
  }

  show(requestedIndex, direction = 'next') {
    const nextIndex = normalizeIndex(requestedIndex, this.slides.length);
    if (nextIndex === this.index) return;
    const outgoing = this.slides[this.index];
    const incoming = this.slides[nextIndex];
    const translate = direction === 'previous' ? '-2rem' : '2rem';

    animate(
      outgoing,
      [
        { opacity: 1, transform: 'scale(1)' },
        { opacity: 0, transform: 'scale(1.03)' },
      ],
      { duration: MOTION.default.duration, easing: MOTION.default.easing },
    );
    outgoing.hidden = true;
    incoming.hidden = false;
    animate(
      incoming,
      [
        { opacity: 0, transform: `translateX(${translate}) scale(1.02)` },
        { opacity: 1, transform: 'translateX(0) scale(1)' },
      ],
      { duration: MOTION.default.duration, easing: MOTION.default.easing },
    );
    this.index = nextIndex;
    this.sync();
    this.startTimer();
  }

  sync() {
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.hidden = !active;
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, index) => {
      const active = index === this.index;
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
    });
  }

  togglePause() {
    this.paused = !this.paused;
    const button = this.querySelector('[data-sleek-pause]');
    button?.setAttribute('aria-pressed', String(this.paused));
    button?.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
    this.paused ? this.stopTimer() : this.startTimer();
  }

  startTimer() {
    this.stopTimer();
    if (this.paused || reducedMotion.matches || this.slides.length < 2) return;
    const delay = Number(this.dataset.autoplay) || MOTION.slideshowAutoplay;
    this.timer = window.setTimeout(() => this.show(nextAutoplayIndex(this.index, this.slides.length)), delay);
  }

  stopTimer() {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = null;
  }
}

class SleekAccordionGroup extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekAccordionReady === 'true') return;
    this.dataset.sleekAccordionReady = 'true';
    this.querySelectorAll('button[aria-controls]').forEach((button) => {
      button.addEventListener('click', () => this.toggle(button));
    });
  }

  toggle(button) {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    const willOpen = button.getAttribute('aria-expanded') !== 'true';
    if (!this.hasAttribute('data-allow-multiple') && willOpen) {
      this.querySelectorAll('button[aria-expanded="true"]').forEach((openButton) => {
        if (openButton !== button) this.setExpanded(openButton, false);
      });
    }
    this.setExpanded(button, willOpen);
  }

  setExpanded(button, expanded) {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    button.setAttribute('aria-expanded', String(expanded));
    if (expanded) {
      panel.hidden = false;
      const height = panel.scrollHeight;
      animate(
        panel,
        [
          { height: '0px', opacity: 0, transform: 'translateY(10px)' },
          { height: `${height}px`, opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 250, easing: 'ease' },
      )?.addEventListener('finish', () => {
        panel.style.height = '';
      });
      return;
    }

    animate(
      panel,
      [
        { height: `${panel.scrollHeight}px`, opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: 250, easing: 'ease' },
    )?.addEventListener('finish', () => {
      panel.hidden = true;
      panel.style.height = '';
    });
    if (reducedMotion.matches) panel.hidden = true;
  }
}

class SleekGridToggle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekGridReady === 'true') return;
    this.dataset.sleekGridReady = 'true';
    this.buttons = [...this.querySelectorAll('button[data-grid-columns]')];
    this.target = document.querySelector(this.dataset.target || '[data-sleek-product-grid]');
    this.buttons.forEach((button) => button.addEventListener('click', () => this.activate(button)));
  }

  activate(button) {
    if (!this.target) return;
    this.target.dataset.gridColumns = button.dataset.gridColumns;
    this.buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
  }
}

class SleekCollectionCatalog extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekCollectionReady === 'true') return;
    this.dataset.sleekCollectionReady = 'true';
    this.drawer = this.querySelector('[data-sleek-filter-drawer]');
    this.openers = [...this.querySelectorAll('[data-sleek-filter-open]')];
    this.closers = [...this.querySelectorAll('[data-sleek-filter-close]')];
    this.openers.forEach((button) => button.addEventListener('click', () => this.openDrawer(button)));
    this.closers.forEach((button) => button.addEventListener('click', () => this.closeDrawer()));
    this.querySelectorAll('[data-sleek-filter-more]').forEach((button) => {
      button.addEventListener('click', () => {
        button.closest('.sleek-collection-filter')?.querySelectorAll('[data-sleek-filter-extra]').forEach((item) => {
          item.hidden = false;
        });
        button.hidden = true;
      });
    });
    this.querySelectorAll('[data-sleek-price-range]').forEach((range) => this.setupPriceRange(range));
    this.querySelector('[data-sleek-desktop-filter-form]')?.addEventListener('change', (event) => {
      if (event.target.matches('input, select')) this.submitCatalogForm(event.currentTarget);
    });
    this.querySelector('[data-sleek-sort]')?.addEventListener('change', (event) => this.submitCatalogForm(event.currentTarget.form));
    this.querySelector('[data-sleek-filter-drawer] form')?.addEventListener('submit', (event) => {
      if (this.dispatchPreviewSubmit(event.currentTarget)) event.preventDefault();
    });
    this.onKeydown = this.onKeydown.bind(this);
  }

  disconnectedCallback() {
    window.clearTimeout(this.filterCloseTimer);
    document.removeEventListener('keydown', this.onKeydown);
    releaseOwnedBodyLock(this);
  }

  setupPriceRange(range) {
    const minRange = range.querySelector('[data-sleek-price-min]');
    const maxRange = range.querySelector('[data-sleek-price-max]');
    const filter = range.closest('.sleek-collection-filter');
    const minInput = filter?.querySelector('[data-sleek-price-min-input]');
    const maxInput = filter?.querySelector('[data-sleek-price-max-input]');
    if (!minRange || !maxRange || !minInput || !maxInput) return;

    const sync = (source) => {
      let min = Number(minRange.value);
      let max = Number(maxRange.value);
      if (min > max) {
        if (source === minRange || source === minInput) max = min;
        else min = max;
      }
      minRange.value = String(min);
      maxRange.value = String(max);
      minInput.value = min === 0 ? '' : String(min);
      maxInput.value = max === Number(maxRange.max) ? '' : String(max);
      const ceiling = Math.max(1, Number(maxRange.max));
      range.style.setProperty('--sleek-price-min', `${(min / ceiling) * 100}%`);
      range.style.setProperty('--sleek-price-max', `${(max / ceiling) * 100}%`);
    };

    minRange.addEventListener('input', () => sync(minRange));
    maxRange.addEventListener('input', () => sync(maxRange));
    minInput.addEventListener('input', () => {
      minRange.value = minInput.value || '0';
      sync(minInput);
    });
    maxInput.addEventListener('input', () => {
      maxRange.value = maxInput.value || maxRange.max;
      sync(maxInput);
    });
    sync();
  }

  dispatchPreviewSubmit(form) {
    if (!form) return false;
    const previewEvent = new CustomEvent('sleek:preview-collection-submit', {
      bubbles: true,
      cancelable: true,
      detail: { form },
    });
    this.dispatchEvent(previewEvent);
    return previewEvent.defaultPrevented;
  }

  submitCatalogForm(form) {
    if (!form || this.dispatchPreviewSubmit(form)) return;
    form.requestSubmit();
  }

  openDrawer(trigger) {
    if (!this.drawer || !this.drawer.hidden) return;
    this.drawerTrigger = trigger;
    this.drawer.hidden = false;
    acquireOwnedBodyLock(this);
    document.addEventListener('keydown', this.onKeydown);
    window.requestAnimationFrame(() => {
      this.drawer.setAttribute('data-open', '');
      this.drawer.querySelector('[data-sleek-filter-close]')?.focus();
    });
  }

  closeDrawer() {
    if (!this.drawer || this.drawer.hidden) return;
    window.clearTimeout(this.filterCloseTimer);
    this.drawer.removeAttribute('data-open');
    document.removeEventListener('keydown', this.onKeydown);
    releaseOwnedBodyLock(this);
    const finish = () => {
      this.drawer.hidden = true;
      this.drawerTrigger?.focus();
    };
    if (reducedMotion.matches) finish();
    else this.filterCloseTimer = window.setTimeout(finish, MOTION.navigation.duration);
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.closeDrawer();
      return;
    }
    if (event.key !== 'Tab' || !this.drawer) return;
    const focusable = [...this.drawer.querySelectorAll('button:not([hidden]), select:not([disabled]), input:not([disabled])')]
      .filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

class SleekCloneSlider extends HTMLElement {
  connectedCallback() {
    if (this.dataset.cloneSliderReady === 'true') return;
    this.dataset.cloneSliderReady = 'true';
    this.track = this.querySelector('[data-sleek-track]');
    this.slides = [...this.querySelectorAll('[data-sleek-slide]')];
    this.index = 0;
    this.physicalIndex = 0;
    this.loopFixToken = 0;
    this.paused = false;
    this.isLoopingHero = this.classList.contains('sleek-home-hero__slider') && this.slides.length > 1;
    this.isLooping = this.isLoopingHero || this.hasAttribute('data-loop');
    if (this.isLooping && this.track && this.slides.length > 1) {
      const before = this.slides.map((slide) => slide.cloneNode(true));
      const after = this.slides.map((slide) => slide.cloneNode(true));
      for (const clone of [...before, ...after]) {
        clone.removeAttribute('data-sleek-slide');
        clone.setAttribute('data-sleek-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
      }
      this.track.prepend(...before);
      this.track.append(...after);
      this.physicalIndex = this.slides.length;
    }
    const previousControl = this.querySelector('[data-sleek-prev]')
      || document.querySelector(`[data-sleek-prev][aria-controls="${this.id}"]`);
    const nextControl = this.querySelector('[data-sleek-next]')
      || document.querySelector(`[data-sleek-next][aria-controls="${this.id}"]`);
    previousControl?.addEventListener('click', () => this.show(this.index - 1));
    nextControl?.addEventListener('click', () => this.show(this.index + 1));
    this.querySelectorAll('[data-sleek-bullet]').forEach((bullet) => {
      bullet.addEventListener('click', () => this.show(Number(bullet.dataset.sleekBullet)));
    });
    this.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.addEventListener('pointermove', (event) => this.onPointerMove(event));
    this.addEventListener('pointerup', (event) => this.finishPointer(event));
    this.addEventListener('pointercancel', (event) => this.finishPointer(event, true));
    this.addEventListener('lostpointercapture', (event) => this.finishPointer(event, true, true));
    this.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
    this.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: true });
    this.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true });
    this.addEventListener('touchcancel', (event) => this.onTouchEnd(event, true), { passive: true });
    this.addEventListener('dragstart', (event) => event.preventDefault());
    this.addEventListener('click', (event) => {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }, true);
    if (!this.isLoopingHero) {
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
    }
    if (this.isLooping && !this.isLoopingHero && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.syncLoopOffset());
      this.resizeObserver.observe(this);
    }
    this.sync();
    this.start();
  }

  disconnectedCallback() {
    this.cancelLoopFix();
    this.stop();
    this.resizeObserver?.disconnect();
  }

  onPointerDown(event) {
    if (event.pointerType === 'touch') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('[data-sleek-bullet]')) return;
    this.pointerId = event.pointerId;
    this.pointerStart = event.clientX;
    this.pointerStartTime = event.timeStamp;
    this.pointerLast = event.clientX;
    this.pointerLastTime = event.timeStamp;
    this.pointerVelocity = 0;
    this.pointerDistance = 0;
    this.pointerCaptured = false;
    this.suppressClick = false;
    this.stop();
    this.style.setProperty('--sleek-drag-offset', '0px');
  }

  onTouchStart(event) {
    if (event.touches.length !== 1 || event.target.closest('[data-sleek-bullet]')) return;
    const touch = event.touches[0];
    this.touchIdentifier = touch.identifier;
    this.touchStart = touch.clientX;
    this.touchStartY = touch.clientY ?? 0;
    this.touchStartTime = event.timeStamp;
    this.touchLast = touch.clientX;
    this.touchLastTime = event.timeStamp;
    this.touchVelocity = 0;
    this.touchDistance = 0;
    this.touchRejected = false;
    this.suppressClick = false;
    this.stop();
    this.style.setProperty('--sleek-drag-offset', '0px');
  }

  onTouchMove(event) {
    if (!Number.isFinite(this.touchStart)) return;
    const touch = [...event.touches].find((item) => item.identifier === this.touchIdentifier);
    if (!touch) return;
    const distance = touch.clientX - this.touchStart;
    const verticalDistance = (touch.clientY ?? this.touchStartY) - this.touchStartY;
    if (!this.touchRejected && Math.abs(verticalDistance) > Math.abs(distance) && Math.abs(verticalDistance) > 8) {
      this.touchRejected = true;
    }
    if (this.touchRejected) return;
    const elapsed = Math.max(1, event.timeStamp - this.touchLastTime);
    this.touchVelocity = (touch.clientX - this.touchLast) / elapsed;
    this.touchLast = touch.clientX;
    this.touchLastTime = event.timeStamp;
    this.touchDistance = distance;
    if (hasPointerDragStarted(distance)) {
      this.toggleAttribute('data-dragging', true);
      this.suppressClick = true;
    }
    const offset = resolveDragOffset(distance, this.getSlideSize());
    this.style.setProperty('--sleek-drag-offset', `${offset}px`);
  }

  onTouchEnd(event, cancelled = false) {
    if (!Number.isFinite(this.touchStart)) return;
    const touch = [...event.changedTouches].find((item) => item.identifier === this.touchIdentifier);
    const distance = cancelled || this.touchRejected || !touch ? 0 : touch.clientX - this.touchStart;
    const nextIndex = resolveDragReleaseIndex(this.index, {
      distance,
      velocity: cancelled || this.touchRejected ? 0 : this.touchVelocity,
      duration: cancelled ? 301 : event.timeStamp - this.touchStartTime,
      slideSize: this.getSlideSize(),
      length: this.slides.length,
    });
    this.touchIdentifier = null;
    this.touchStart = null;
    this.touchStartY = null;
    this.touchDistance = 0;
    this.touchRejected = false;
    this.toggleAttribute('data-dragging', false);
    this.style.setProperty('--sleek-drag-offset', '0px');
    if (nextIndex === this.index) this.show(this.index);
    else if (nextIndex === normalizeIndex(this.index + 1, this.slides.length)) this.show(this.index + 1);
    else this.show(this.index - 1);
  }

  onPointerMove(event) {
    if (!Number.isFinite(this.pointerStart) || event.pointerId !== this.pointerId) return;
    const elapsed = Math.max(1, event.timeStamp - this.pointerLastTime);
    const delta = event.clientX - this.pointerLast;
    this.pointerVelocity = delta / elapsed;
    this.pointerLast = event.clientX;
    this.pointerLastTime = event.timeStamp;
    this.pointerDistance = event.clientX - this.pointerStart;
    if (hasPointerDragStarted(this.pointerDistance) && !this.pointerCaptured) {
      this.pointerCaptured = true;
      this.setPointerCapture?.(event.pointerId);
      this.toggleAttribute('data-dragging', true);
    }
    const slideSize = this.getSlideSize();
    const offset = resolveDragOffset(this.pointerDistance, slideSize);
    this.style.setProperty('--sleek-drag-offset', `${offset}px`);
    if (hasPointerDragStarted(this.pointerDistance)) this.suppressClick = true;
  }

  finishPointer(event, cancelled = false, captureLost = false) {
    if (!Number.isFinite(this.pointerStart) || event.pointerId !== this.pointerId) return;
    const distance = cancelled ? 0 : event.clientX - this.pointerStart;
    const nextIndex = resolveDragReleaseIndex(this.index, {
      distance,
      velocity: cancelled ? 0 : this.pointerVelocity,
      duration: cancelled ? 301 : event.timeStamp - this.pointerStartTime,
      slideSize: this.getSlideSize(),
      length: this.slides.length,
    });
    const pointerCaptured = this.pointerCaptured;
    this.pointerStart = null;
    this.pointerId = null;
    this.pointerDistance = 0;
    this.pointerCaptured = false;
    if (!captureLost && pointerCaptured && this.hasPointerCapture?.(event.pointerId)) {
      this.releasePointerCapture?.(event.pointerId);
    }
    this.toggleAttribute('data-dragging', false);
    this.style.setProperty('--sleek-drag-offset', '0px');
    if (nextIndex === this.index) this.show(this.index);
    else if (nextIndex === normalizeIndex(this.index + 1, this.slides.length)) this.show(this.index + 1);
    else this.show(this.index - 1);
  }

  getSlideSize() {
    const slide = this.track?.querySelector('[data-sleek-slide], [data-sleek-clone]');
    if (!slide) return this.clientWidth;
    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  show(requestedIndex) {
    if (!this.slides.length) return;
    this.cancelLoopFix();
    const nextIndex = normalizeIndex(requestedIndex, this.slides.length);
    let loopFixIndex = null;
    if (this.isLooping) {
      if (requestedIndex < 0 && this.index === 0) {
        this.physicalIndex -= 1;
        loopFixIndex = this.slides.length + nextIndex;
      } else if (requestedIndex >= this.slides.length && this.index === this.slides.length - 1) {
        this.physicalIndex += 1;
        loopFixIndex = this.slides.length + nextIndex;
      } else {
        this.physicalIndex = this.slides.length + nextIndex;
      }
    }
    this.index = nextIndex;
    this.sync();
    if (loopFixIndex !== null) {
      const loopFixToken = this.loopFixToken;
      const applyLoopFix = (event) => {
        if (event && (event.target !== this.track || event.propertyName !== 'transform')) return;
        if (this.loopFixToken !== loopFixToken) return;
        this.cancelLoopFix();
        this.toggleAttribute('data-loop-fix', true);
        this.physicalIndex = loopFixIndex;
        this.sync();
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.toggleAttribute('data-loop-fix', false)));
      };
      this.loopFixHandler = applyLoopFix;
      this.track?.addEventListener('transitionend', applyLoopFix);
      this.loopFixTimer = window.setTimeout(() => applyLoopFix(), MOTION.default.duration + 80);
    }
    this.start();
  }

  cancelLoopFix() {
    this.loopFixToken = (this.loopFixToken || 0) + 1;
    if (this.loopFixTimer) window.clearTimeout(this.loopFixTimer);
    this.loopFixTimer = null;
    if (this.loopFixHandler) this.track?.removeEventListener('transitionend', this.loopFixHandler);
    this.loopFixHandler = null;
  }

  sync() {
    this.style.setProperty('--sleek-active-index', String(this.index));
    if (this.isLooping) this.style.setProperty('--sleek-physical-index', String(this.physicalIndex));
    this.syncLoopOffset();
    this.warmImages();
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.setAttribute('aria-hidden', String(!active));
      slide.toggleAttribute('data-active', active);
    });
    const fractions = [
      ...this.querySelectorAll('[data-sleek-fraction]'),
      ...document.querySelectorAll(`[data-sleek-fraction][aria-controls="${this.id}"]`),
    ];
    fractions.forEach((fraction) => { fraction.textContent = `${this.index + 1} / ${this.slides.length}`; });
    this.querySelectorAll('[data-sleek-bullet]').forEach((bullet, index) => {
      bullet.setAttribute('aria-current', String(index === this.index));
    });
  }

  warmImages() {
    if (!this.slides.length) return;
    const warmIndexes = new Set([
      normalizeIndex(this.index - 1, this.slides.length),
      this.index,
      normalizeIndex(this.index + 1, this.slides.length),
    ]);
    this.slides.forEach((slide, index) => {
      if (!warmIndexes.has(index)) return;
      slide.querySelectorAll?.('img').forEach((image) => {
        image.loading = 'eager';
        image.setAttribute('fetchpriority', index === this.index ? 'high' : 'auto');
      });
    });
  }

  syncLoopOffset() {
    if (!this.isLooping || this.isLoopingHero) return;
    const step = this.getSlideSize();
    if (step) this.style.setProperty('--sleek-loop-offset', `${this.physicalIndex * -step}px`);
  }

  start() {
    this.stop();
    const delay = Number(this.dataset.autoplay);
    if (!delay || reducedMotion.matches || this.slides.length < 2 || this.paused) return;
    this.timer = window.setTimeout(() => this.show(this.index + 1), delay);
  }

  stop() {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = null;
  }
}

class SleekCloneTabs extends SleekTabs {}

class SleekCloneAccordion extends SleekAccordionGroup {
  setExpanded(button, expanded) {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    const content = panel.firstElementChild;
    button.setAttribute('aria-expanded', String(expanded));
    if (expanded) {
      panel.hidden = false;
      const panelAnimation = animate(
        panel,
        [{ height: '0px' }, { height: `${panel.scrollHeight}px` }],
        { duration: 250, easing: 'ease' },
      );
      animate(
        content,
        [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 150, easing: 'ease' },
      );
      panelAnimation?.addEventListener('finish', () => { panel.style.height = ''; }, { once: true });
      return;
    }
    animate(
      content,
      [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(10px)' }],
      { duration: 150, easing: 'ease' },
    );
    const panelAnimation = animate(
      panel,
      [{ height: `${panel.scrollHeight}px` }, { height: '0px' }],
      { duration: 250, easing: 'ease' },
    );
    const finish = () => {
      panel.hidden = true;
      panel.style.height = '';
    };
    if (panelAnimation) panelAnimation.addEventListener('finish', finish, { once: true });
    else finish();
  }
}

class SleekCloneRail extends HTMLElement {
  connectedCallback() {
    if (this.dataset.cloneRailReady === 'true') return;
    this.dataset.cloneRailReady = 'true';
    if (!this.id) this.id = `sleek-rail-${Math.random().toString(36).slice(2, 9)}`;
    this.items = [...this.children];
    this.usesDesktopLoop = this.hasAttribute('data-desktop-loop');
    this.desktopLoopMedia = window.matchMedia('(min-width: 768px)');
    this.handleLoopMediaChange = () => this.refreshLoop();
    if (this.usesDesktopLoop) {
      this.desktopLoopMedia.addEventListener('change', this.handleLoopMediaChange);
    }
    this.refreshLoop();
    this.controls = [...document.querySelectorAll(`[aria-controls="${this.id}"]`)];
    this.pagination = [...this.parentElement.querySelectorAll('[data-sleek-hot-pagination]')];
    this.counter = this.parentElement.querySelector('[data-sleek-rail-counter]');
    this.controls.forEach((control) => {
      const direction = control.hasAttribute('data-sleek-rail-prev') ? -1 : 1;
      control.addEventListener('click', () => this.scrollByPage(direction));
    });
    this.pagination.forEach((button) => {
      button.addEventListener('click', () => this.scrollToIndex(Number(button.dataset.sleekHotPagination)));
    });
    this.addEventListener('pointerdown', (event) => this.startDrag(event));
    this.addEventListener('pointermove', (event) => this.moveDrag(event));
    const finish = (event) => this.finishDrag(event);
    this.addEventListener('pointerup', finish);
    this.addEventListener('pointercancel', (event) => this.finishDrag(event, true));
    this.addEventListener('lostpointercapture', finish);
    this.addEventListener('dragstart', (event) => event.preventDefault());
    this.addEventListener('click', (event) => {
      if (!this.dragMoved) return;
      event.preventDefault();
      event.stopPropagation();
      this.dragMoved = false;
    }, true);
    this.addEventListener('scroll', () => window.requestAnimationFrame(() => {
      this.syncControls();
      this.syncPagination();
    }), { passive: true });
    this.addEventListener('scrollend', () => {
      if (!this.hasAttribute('data-settling')) this.fixLoopPosition();
    });
    this.syncControls();
    this.syncPagination();
  }

  disconnectedCallback() {
    if (this.usesDesktopLoop) {
      this.desktopLoopMedia?.removeEventListener('change', this.handleLoopMediaChange);
    }
    if (this.loopRefreshFrame) window.cancelAnimationFrame(this.loopRefreshFrame);
    if (this.scrollAnimationFrame) window.cancelAnimationFrame(this.scrollAnimationFrame);
    this.toggleAttribute('data-settling', false);
  }

  refreshLoop() {
    const previousStep = this.getItemStep();
    const previousPhysicalIndex = previousStep ? Math.round(this.scrollLeft / previousStep) : 0;
    const previousLogicalIndex = this.items.length
      ? normalizeIndex(previousPhysicalIndex - (this.loop ? this.items.length : 0), this.items.length)
      : 0;
    this.querySelectorAll('[data-sleek-rail-clone]').forEach((clone) => clone.remove());
    const desktopLoop = this.hasAttribute('data-desktop-loop') && this.desktopLoopMedia.matches;
    this.loop = (this.hasAttribute('data-loop') || desktopLoop) && this.items.length > 1;
    if (this.loop) {
      const before = this.items.map((item) => item.cloneNode(true));
      const after = this.items.map((item) => item.cloneNode(true));
      [...before, ...after].forEach((clone) => {
        clone.setAttribute('data-sleek-rail-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
      });
      this.prepend(...before);
      this.append(...after);
    }
    if (this.loopRefreshFrame) window.cancelAnimationFrame(this.loopRefreshFrame);
    this.loopRefreshFrame = window.requestAnimationFrame(() => {
      this.loopRefreshFrame = null;
      const step = this.getItemStep();
      this.scrollLeft = step * ((this.loop ? this.items.length : 0) + previousLogicalIndex);
      this.syncControls();
      this.syncPagination();
    });
  }

  startDrag(event) {
    if (this.hasAttribute('data-disable-drag')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('button, input, select, textarea, [contenteditable="true"]')) return;
    this.cancelScrollAnimation();
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragLastX = event.clientX;
    this.dragLastTime = event.timeStamp;
    this.dragVelocity = 0;
    this.dragStartScroll = this.scrollLeft;
    this.dragCaptured = false;
    this.dragMoved = false;
  }

  moveDrag(event) {
    if (!Number.isFinite(this.dragStartX) || event.pointerId !== this.dragPointerId) return;
    const elapsed = Math.max(1, event.timeStamp - this.dragLastTime);
    this.dragVelocity = (event.clientX - this.dragLastX) / elapsed;
    this.dragLastX = event.clientX;
    this.dragLastTime = event.timeStamp;
    const distance = event.clientX - this.dragStartX;
    this.scrollLeft = this.dragStartScroll - distance;
    if (hasPointerDragStarted(distance)) {
      if (!this.dragCaptured) {
        this.dragCaptured = true;
        this.setPointerCapture?.(event.pointerId);
        this.toggleAttribute('data-dragging', true);
      }
      this.dragMoved = true;
      if (event.cancelable) event.preventDefault();
    }
  }

  finishDrag(event, cancelled = false, captureLost = false) {
    if (!Number.isFinite(this.dragStartX) || event.pointerId !== this.dragPointerId) return;
    const pointerId = this.dragPointerId;
    const step = this.getItemStep();
    const targetIndex = cancelled
      ? Math.round(this.dragStartScroll / step)
      : resolveRailSnapIndex({
        startScroll: this.dragStartScroll,
        currentScroll: this.scrollLeft,
        velocity: this.dragVelocity,
        step,
      });
    const dragCaptured = this.dragCaptured;
    this.dragPointerId = null;
    this.dragStartX = null;
    this.dragLastX = null;
    this.dragLastTime = null;
    this.dragVelocity = 0;
    this.dragStartScroll = null;
    this.dragCaptured = false;
    if (cancelled) this.dragMoved = false;
    this.toggleAttribute('data-dragging', false);
    if (!captureLost && dragCaptured && this.hasPointerCapture?.(pointerId)) this.releasePointerCapture?.(pointerId);
    if (step) this.animateScrollTo(targetIndex * step);
    else this.syncControls();
    if (this.dragMoved) {
      window.clearTimeout(this.dragMovedResetTimer);
      this.dragMovedResetTimer = window.setTimeout(() => {
        this.dragMoved = false;
      }, 0);
    }
  }

  scrollByPage(direction) {
    const first = this.firstElementChild;
    if (!first) return;
    const styles = getComputedStyle(this);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const itemWidth = first.getBoundingClientRect().width + gap;
    this.animateScrollTo(this.scrollLeft + direction * itemWidth);
  }

  getItemStep() {
    const first = this.items?.[0] || this.firstElementChild;
    if (!first) return 0;
    const styles = getComputedStyle(this);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  jumpToMiddle() {
    if (!this.loop) return;
    this.scrollLeft = this.getItemStep() * this.items.length;
    this.syncControls();
    this.syncPagination();
  }

  scrollToIndex(index) {
    const step = this.getItemStep();
    if (!step || !this.items.length) return;
    const logicalIndex = normalizeIndex(index, this.items.length);
    const baseIndex = this.loop ? this.items.length : 0;
    this.animateScrollTo(step * (baseIndex + logicalIndex));
  }

  animateScrollTo(targetLeft) {
    this.cancelScrollAnimation();
    const startLeft = this.scrollLeft;
    const distance = targetLeft - startLeft;
    if (reducedMotion.matches || Math.abs(distance) < 1) {
      this.scrollLeft = targetLeft;
      this.fixLoopPosition();
      return;
    }
    this.toggleAttribute('data-settling', true);
    const startTime = performance.now();
    const duration = Math.min(560, Math.max(420, Math.abs(distance) * 0.45));
    const tick = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const eased = 1 - ((1 - progress) ** 5);
      this.scrollLeft = startLeft + distance * eased;
      if (progress < 1) {
        this.scrollAnimationFrame = window.requestAnimationFrame(tick);
        return;
      }
      this.scrollAnimationFrame = null;
      this.scrollLeft = targetLeft;
      this.fixLoopPosition();
      this.toggleAttribute('data-settling', false);
    };
    this.scrollAnimationFrame = window.requestAnimationFrame(tick);
  }

  cancelScrollAnimation() {
    if (this.scrollAnimationFrame) window.cancelAnimationFrame(this.scrollAnimationFrame);
    this.scrollAnimationFrame = null;
    this.toggleAttribute('data-settling', false);
  }

  fixLoopPosition() {
    if (!this.loop) return;
    const groupWidth = this.getItemStep() * this.items.length;
    if (!groupWidth) return;
    if (this.scrollLeft < groupWidth * 0.5) this.scrollLeft += groupWidth;
    else if (this.scrollLeft >= groupWidth * 1.5) this.scrollLeft -= groupWidth;
    this.syncPagination();
  }

  syncPagination() {
    if (!this.items?.length) return;
    const step = this.getItemStep();
    if (!step) return;
    const physicalIndex = Math.round(this.scrollLeft / step);
    const logicalIndex = normalizeIndex(physicalIndex - (this.loop ? this.items.length : 0), this.items.length);
    this.pagination?.forEach((button, index) => button.setAttribute('aria-current', String(index === logicalIndex)));
    if (this.counter) this.counter.textContent = `${logicalIndex + 1} / ${this.items.length}`;
  }

  syncControls() {
    const maxScroll = Math.max(0, this.scrollWidth - this.clientWidth);
    this.controls?.forEach((control) => {
      const previous = control.hasAttribute('data-sleek-rail-prev');
      control.disabled = this.loop ? false : (previous ? this.scrollLeft <= 2 : this.scrollLeft >= maxScroll - 2);
    });
  }
}

class SleekSocialMedia extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekSocialMediaReady === 'true') return;
    this.dataset.sleekSocialMediaReady = 'true';
    this.video = this.querySelector('video');
    this.button = this.querySelector('[data-sleek-video-toggle]');
    if (!this.video) return;
    this.button?.addEventListener('click', () => this.toggleVideo());
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) this.play();
        else this.pause();
      }, { threshold: 0.35 });
      this.observer.observe(this);
    } else this.play();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  play() {
    this.video?.play().catch(() => {});
    if (this.button) {
      this.button.setAttribute('aria-label', 'Pause video');
      const playIcon = this.button.querySelector('[data-sleek-video-icon-play]');
      const pauseIcon = this.button.querySelector('[data-sleek-video-icon-pause]');
      if (playIcon) playIcon.hidden = true;
      if (pauseIcon) pauseIcon.hidden = false;
    }
  }

  pause() {
    this.video?.pause();
    if (this.button) {
      this.button.setAttribute('aria-label', 'Play video');
      const playIcon = this.button.querySelector('[data-sleek-video-icon-play]');
      const pauseIcon = this.button.querySelector('[data-sleek-video-icon-pause]');
      if (playIcon) playIcon.hidden = false;
      if (pauseIcon) pauseIcon.hidden = true;
    }
  }

  toggleVideo() {
    if (this.video?.paused) this.play();
    else this.pause();
  }
}

class SleekProductsBundle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekBundleReady === 'true') return;
    this.dataset.sleekBundleReady = 'true';
    this.targets = [...this.querySelectorAll('[data-sleek-hotspot], [data-sleek-bundle-item]')];
    this.targets.forEach((target) => {
      const id = target.dataset.sleekHotspot || target.dataset.sleekBundleItem;
      target.addEventListener('mouseenter', () => this.activate(id));
      target.addEventListener('mouseleave', () => this.clearActive());
      target.addEventListener('focusin', () => this.activate(id));
    });
    this.querySelectorAll('.sleek-bundle__variant').forEach((select) => {
      select.addEventListener('change', () => this.updateVariantPrice(select));
      this.updateVariantPrice(select);
    });
    this.addEventListener('mouseleave', () => this.clearActive());
    this.querySelectorAll('[data-sleek-hotspot]').forEach((hotspot) => {
      hotspot.addEventListener('click', (event) => {
        if (!window.matchMedia('(hover: none)').matches || hotspot.classList.contains('is-active')) return;
        event.preventDefault();
        this.activate(hotspot.dataset.sleekHotspot);
      });
    });
    this.querySelector('[data-sleek-bundle-add]')?.addEventListener('click', () => this.addAll());
  }

  activate(id) {
    this.classList.add('is-hover');
    this.targets.forEach((target) => {
      const targetId = target.dataset.sleekHotspot || target.dataset.sleekBundleItem;
      target.classList.toggle('is-active', targetId === id);
    });
  }

  clearActive() {
    this.classList.remove('is-hover');
    this.targets.forEach((target) => target.classList.remove('is-active'));
  }

  updateVariantPrice(select) {
    const option = select.selectedOptions?.[0];
    const priceNode = select.closest('[data-sleek-bundle-item]')?.querySelector('[data-sleek-bundle-price]');
    if (!option || !priceNode) return;
    const price = Number(option.dataset.price || 0);
    const compareAt = Number(option.dataset.compare_at_price || 0);
    const formatMoney = (cents) => {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
      return `$${(cents / 100).toFixed(2)}`;
    };
    priceNode.textContent = price ? formatMoney(price) : '';
    if (compareAt > price) {
      const compareNode = document.createElement('s');
      compareNode.textContent = formatMoney(compareAt);
      priceNode.append(' ', compareNode);
    }
    select.closest('[data-sleek-bundle-item]')?.classList.toggle('is-sold-out', option.dataset.available === 'false');
  }

  async addAll() {
    const button = this.querySelector('[data-sleek-bundle-add]');
    const error = this.querySelector('[data-sleek-bundle-error]');
    if (!button || button.dataset.sleekBusy === 'true') return;
    const items = [...this.querySelectorAll('select[name="id"]')]
      .map((select) => Number(select.value) || Number(select.selectedOptions?.[0]?.dataset.sourceVariantId))
      .filter(Boolean)
      .map((id) => ({ id, quantity: 1 }));
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    if (!items.length) {
      if (error) {
        error.textContent = 'Choose available products before adding this bundle.';
        error.hidden = false;
      }
      return;
    }
    button.dataset.sleekBusy = 'true';
    button.setAttribute('aria-busy', 'true');
    try {
      const previewEvent = new CustomEvent('sleek:preview-add-items', {
        bubbles: true,
        cancelable: true,
        detail: { items, source: 'bundle' },
      });
      document.dispatchEvent(previewEvent);
      if (previewEvent.defaultPrevented) return;
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error('Unable to add bundle');
      await response.json();
      await refreshCartDrawer();
      const drawer = document.querySelector('#cart-drawer');
      if (typeof drawer?.open === 'function') drawer.open();
      else drawer?.querySelector('dialog')?.showModal?.();
    } catch {
      if (error) {
        error.textContent = 'The bundle could not be added. Please try again.';
        error.hidden = false;
      }
    } finally {
      button.dataset.sleekBusy = 'false';
      button.removeAttribute('aria-busy');
    }
  }
}

class SleekCloneDrawer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.cloneDrawerReady === 'true') return;
    this.dataset.cloneDrawerReady = 'true';
    this.history = ['root'];
    document.querySelectorAll(`[data-sleek-drawer-open="${this.id}"]`).forEach((button) => {
      button.addEventListener('click', () => this.open(button));
    });
    this.querySelectorAll('[data-sleek-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
    this.querySelectorAll('[data-sleek-drawer-view-open]').forEach((button) => {
      button.addEventListener('click', () => this.showView(button.dataset.sleekDrawerViewOpen, true));
    });
    this.querySelectorAll('[data-sleek-drawer-back]').forEach((button) => {
      button.addEventListener('click', () => this.back());
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
      if (event.key === 'Tab') this.trapFocus(event);
    });
  }

  disconnectedCallback() {
    window.clearTimeout(this.closeTimer);
    releaseOwnedBodyLock(this);
  }

  open(trigger) {
    if (!this.hidden && this.dataset.sleekClosing !== 'true') return;
    window.clearTimeout(this.closeTimer);
    this.dataset.sleekClosing = 'false';
    this.trigger = trigger;
    this.hidden = false;
    this.showView('root', false);
    this.history = ['root'];
    trigger?.setAttribute('aria-expanded', 'true');
    acquireOwnedBodyLock(this);
    window.requestAnimationFrame(() => {
      this.toggleAttribute('data-open', true);
      const initialFocus = this.querySelector('[data-sleek-drawer-autofocus]')
        || this.querySelector('[data-sleek-drawer-close]:not([tabindex="-1"])');
      initialFocus?.focus();
    });
  }

  close() {
    if (this.hidden || this.dataset.sleekClosing === 'true') return;
    this.dataset.sleekClosing = 'true';
    this.toggleAttribute('data-open', false);
    this.showView('root', false);
    this.history = ['root'];
    this.trigger?.setAttribute('aria-expanded', 'false');
    releaseOwnedBodyLock(this);
    const finish = () => {
      this.hidden = true;
      this.dataset.sleekClosing = 'false';
      this.trigger?.focus();
    };
    if (reducedMotion.matches) finish();
    else this.closeTimer = window.setTimeout(finish, MOTION.default.duration);
  }

  showView(name, pushHistory) {
    const target = this.querySelector(`[data-sleek-drawer-view="${name}"]`);
    if (!target) return;
    this.querySelectorAll('[data-sleek-drawer-view]').forEach((view) => {
      view.hidden = view !== target;
    });
    if (pushHistory && this.history.at(-1) !== name) this.history.push(name);
  }

  back() {
    if (this.history.length > 1) this.history.pop();
    this.showView(this.history.at(-1) || 'root', false);
  }

  trapFocus(event) {
    const focusable = [...this.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])')]
      .filter((node) => !node.hidden && node.getClientRects().length);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

class SleekCloneHeader extends HTMLElement {
  connectedCallback() {
    if (this.dataset.cloneHeaderReady === 'true') return;
    this.dataset.cloneHeaderReady = 'true';
    this.triggers = [...this.querySelectorAll('[data-sleek-mega-trigger]')];
    this.panels = [...this.querySelectorAll('[data-sleek-mega]')];
    this.backdrop = this.querySelector('[data-sleek-mega-backdrop]');
    this.triggers.forEach((trigger) => {
      const name = trigger.dataset.sleekMegaTrigger;
      trigger.addEventListener('mouseenter', () => this.openMega(name));
      trigger.addEventListener('focus', () => this.openMega(name));
    });
    this.addEventListener('mouseleave', () => this.scheduleClose());
    this.addEventListener('mouseenter', () => window.clearTimeout(this.closeTimer));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.scheduleClose();
    });
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.closeMega();
    };
    document.addEventListener('click', this.onDocumentClick);
    this.backdrop?.addEventListener('mouseenter', () => this.closeMega());
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeMega();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    window.clearTimeout(this.closeTimer);
  }

  openMega(name) {
    window.clearTimeout(this.closeTimer);
    let found = false;
    this.panels.forEach((panel) => {
      const active = panel.dataset.sleekMega === name;
      panel.hidden = !active;
      found ||= active;
    });
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', String(trigger.dataset.sleekMegaTrigger === name)));
    if (this.backdrop) this.backdrop.hidden = !found;
  }

  scheduleClose() {
    this.closeTimer = window.setTimeout(() => this.closeMega(), 160);
  }

  closeMega() {
    this.panels.forEach((panel) => { panel.hidden = true; });
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    if (this.backdrop) this.backdrop.hidden = true;
  }
}

class SleekQuickView extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekQuickViewReady === 'true') return;
    this.dataset.sleekQuickViewReady = 'true';
    this.querySelectorAll('[data-sleek-quick-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.variantSelect = this.querySelector('[data-sleek-quick-variant]');
    this.quantityInput = this.querySelector('[data-sleek-quick-quantity]');
    this.addButton = this.querySelector('[data-sleek-quick-add]');
    this.buyButton = this.querySelector('[data-sleek-quick-buy]');
    this.status = this.querySelector('[data-sleek-quick-status]');
    this.variantSelect?.addEventListener('change', () => this.syncQuickVariant());
    this.quantityInput?.addEventListener('change', () => this.adjustQuantity(0));
    this.querySelector('[data-sleek-quick-quantity-minus]')?.addEventListener('click', () => this.adjustQuantity(-1));
    this.querySelector('[data-sleek-quick-quantity-plus]')?.addEventListener('click', () => this.adjustQuantity(1));
    this.addButton?.addEventListener('click', () => this.addSelected());
    this.buyButton?.addEventListener('click', () => this.addSelected(true));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
      if (event.key === 'Tab') this.trapFocus(event);
    });
  }

  disconnectedCallback() {
    window.clearTimeout(this.closeTimer);
    releaseOwnedBodyLock(this);
  }

  open(trigger) {
    window.clearTimeout(this.closeTimer);
    const card = trigger.closest('[data-sleek-product-card], .sleek-social-card');
    const sourceImage = card?.querySelector('.sleek-source-card__media > img:first-child, .sleek-social-card__product img');
    const title = card?.querySelector('h3 a, .sleek-social-card__product a > span')?.textContent?.trim()
      || trigger.getAttribute('aria-label')?.replace(/^(Quick view|Choose options for|Add)\s*/i, '')
      || 'Product';
    const type = card?.querySelector('.sleek-source-card__type')?.textContent?.replace(/^Type:\s*/i, '').trim() || '';
    const price = card?.querySelector('.sleek-source-card__price, .sleek-social-card__product strong')?.textContent?.trim() || '';
    const productUrl = trigger.dataset.productUrl || card?.querySelector('h3 a, .sleek-social-card__product a')?.getAttribute('href') || '#';
    const cardVariants = card?.querySelector('[data-sleek-card-variants]');
    const image = this.querySelector('[data-sleek-quick-image]');
    if (image && sourceImage) {
      image.src = sourceImage.currentSrc || sourceImage.src;
      image.alt = title;
    }
    const titleNode = this.querySelector('[data-sleek-quick-title]');
    const typeNode = this.querySelector('[data-sleek-quick-type]');
    const priceNode = this.querySelector('[data-sleek-quick-price]');
    const link = this.querySelector('[data-sleek-quick-link]');
    if (titleNode) titleNode.textContent = title;
    if (typeNode) typeNode.textContent = type;
    if (priceNode) priceNode.textContent = price;
    if (link) link.href = productUrl;
    if (this.variantSelect) {
      const options = [...(cardVariants?.options || [])].map((option) => option.cloneNode(true));
      if (!options.length && trigger.dataset.variantId) {
        const option = document.createElement('option');
        option.value = trigger.dataset.variantId;
        option.textContent = 'Default';
        option.dataset.available = 'true';
        options.push(option);
      }
      this.variantSelect.replaceChildren(...options);
      this.variantSelect.selectedIndex = Math.max(0, options.findIndex((option) => option.selected));
      this.variantSelect.closest('.sleek-quick-view__variant-field')?.toggleAttribute('hidden', options.length < 2 && options[0]?.textContent?.trim() === 'Default Title');
    }
    if (this.quantityInput) this.quantityInput.value = '1';
    if (this.status) {
      this.status.hidden = true;
      this.status.textContent = '';
    }
    this.syncQuickVariant();
    this.trigger = trigger;
    this.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    acquireOwnedBodyLock(this);
    window.requestAnimationFrame(() => {
      this.toggleAttribute('data-open', true);
      this.querySelector('[data-sleek-quick-close]:not([tabindex="-1"])')?.focus();
    });
  }

  syncQuickVariant() {
    const option = this.variantSelect?.selectedOptions?.[0];
    const available = Boolean(option) && option.dataset.available !== 'false';
    const price = Number(option?.dataset.price);
    const priceNode = this.querySelector('[data-sleek-quick-price]');
    if (priceNode && Number.isFinite(price) && price > 0) {
      priceNode.textContent = window.Shopify?.formatMoney
        ? window.Shopify.formatMoney(price)
        : `$${(price / 100).toFixed(2)}`;
    }
    [this.addButton, this.buyButton].forEach((button) => {
      if (!button) return;
      button.disabled = !available;
      button.dataset.variantId = option?.value || '';
    });
  }

  adjustQuantity(delta) {
    if (!this.quantityInput) return;
    const current = Number.parseInt(this.quantityInput.value, 10) || 1;
    this.quantityInput.value = String(Math.max(1, current + delta));
  }

  async addSelected(checkout = false) {
    const item = buildQuickViewItem(this.variantSelect?.value, this.quantityInput?.value);
    if (!item || this.addButton?.disabled) return;
    if (this.status) {
      this.status.hidden = true;
      this.status.textContent = '';
    }
    [this.addButton, this.buyButton].forEach((button) => {
      if (!button) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    });
    try {
      const previewEvent = new CustomEvent('sleek:preview-add-items', {
        bubbles: true,
        cancelable: true,
        detail: { items: [item], source: checkout ? 'quick-view-buy-now' : 'quick-view' },
      });
      document.dispatchEvent(previewEvent);
      if (previewEvent.defaultPrevented) {
        this.close();
        return;
      }
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      if (!response.ok) throw new Error('Unable to add product');
      await response.json();
      this.close();
      if (checkout) {
        window.location.assign('/checkout');
        return;
      }
      await refreshCartDrawer();
      const drawer = document.querySelector('#cart-drawer');
      if (typeof drawer?.open === 'function') drawer.open();
      else drawer?.querySelector('dialog')?.showModal?.();
    } catch {
      if (this.status) {
        this.status.textContent = 'This product could not be added. Please try again.';
        this.status.hidden = false;
      }
    } finally {
      this.syncQuickVariant();
      [this.addButton, this.buyButton].forEach((button) => button?.removeAttribute('aria-busy'));
    }
  }

  close() {
    if (this.hidden) return;
    this.toggleAttribute('data-open', false);
    releaseOwnedBodyLock(this);
    const trigger = this.trigger;
    const finish = () => {
      this.hidden = true;
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.focus();
    };
    if (reducedMotion.matches) finish();
    else this.closeTimer = window.setTimeout(finish, MOTION.fast.duration);
  }

  trapFocus(event) {
    const focusable = [...this.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), a[href], input:not([disabled]), select:not([disabled])')]
      .filter((node) => !node.hidden && node.getClientRects().length);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

class SleekPageAccordion extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekPageAccordionReady === 'true') return;
    this.dataset.sleekPageAccordionReady = 'true';
    this.items = [...this.querySelectorAll(':scope > details')];
    this.items.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (item.open) this.items.forEach((other) => {
          if (other !== item) other.open = false;
        });
      });
    });
  }
}

class SleekFooterAccordion extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekFooterAccordionReady === 'true') return;
    this.dataset.sleekFooterAccordionReady = 'true';
    this.items = [...this.querySelectorAll(':scope > details')];
    this.desktopMedia = window.matchMedia('(min-width: 1024px)');
    this.handleMediaChange = () => this.sync();
    this.desktopMedia.addEventListener('change', this.handleMediaChange);
    this.querySelectorAll('summary').forEach((summary) => {
      summary.addEventListener('click', (event) => {
        if (this.desktopMedia.matches) event.preventDefault();
      });
    });
    this.sync();
  }

  disconnectedCallback() {
    this.desktopMedia?.removeEventListener('change', this.handleMediaChange);
  }

  sync() {
    this.items.forEach((item) => {
      item.open = this.desktopMedia.matches;
      item.querySelector('summary')?.setAttribute('aria-disabled', String(this.desktopMedia.matches));
    });
  }
}

class SleekLocalizationDisclosure extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekLocalizationReady === 'true') return;
    this.dataset.sleekLocalizationReady = 'true';
    this.toggleButton = this.querySelector('[data-sleek-country-toggle]');
    this.list = this.querySelector('[data-sleek-country-list]');
    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-sleek-country-input]');
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    this.toggleButton?.addEventListener('click', () => this.toggle());
    this.querySelectorAll('[data-sleek-country-option]').forEach((option) => {
      option.addEventListener('click', (event) => this.select(event, option));
    });
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  toggle() {
    const open = this.toggleButton?.getAttribute('aria-expanded') !== 'true';
    this.setOpen(open);
  }

  setOpen(open) {
    if (!this.toggleButton || !this.list) return;
    this.toggleButton.setAttribute('aria-expanded', String(open));
    this.list.hidden = !open;
    this.toggleAttribute('data-open', open);
    if (open) window.requestAnimationFrame(() => this.list.querySelector('[data-sleek-country-option]')?.focus());
  }

  close() {
    this.setOpen(false);
  }

  select(event, option) {
    event.preventDefault();
    if (!this.form || !this.input || !option.dataset.value) return;
    this.input.value = option.dataset.value;
    this.close();
    this.form.requestSubmit();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.toggleButton?.focus();
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.list?.hidden) return;
    const options = [...this.querySelectorAll('[data-sleek-country-option]')];
    if (!options.length) return;
    event.preventDefault();
    const activeIndex = options.indexOf(document.activeElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    options[normalizeIndex(activeIndex + step, options.length)].focus();
  }
}

class SleekSubscriptionPopup extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekPopupReady === 'true') return;
    this.dataset.sleekPopupReady = 'true';
    this.storageKey = 'sleektheme:popup';
    this.closeButtons = [...this.querySelectorAll('[data-sleek-popup-close]')];
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    if (window.Shopify?.designMode || this.wasRecentlyOpened()) return;
    const delay = Number(this.dataset.delay) || 5000;
    this.openTimer = window.setTimeout(() => this.open(), delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    releaseOwnedBodyLock(this);
  }

  wasRecentlyOpened() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
      return Number(saved?.expiresAt) > Date.now();
    } catch {
      return false;
    }
  }

  rememberOpen() {
    try {
      const repeat = Number(this.dataset.repeatMs) || 86400000;
      window.localStorage.setItem(this.storageKey, JSON.stringify({ opened: true, expiresAt: Date.now() + repeat }));
    } catch {
      // A blocked storage API must not prevent the visible popup interaction.
    }
  }

  open() {
    if (!this.hidden) return;
    window.clearTimeout(this.closeTimer);
    this.closing = false;
    this.returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.hidden = false;
    this.rememberOpen();
    acquireOwnedBodyLock(this);
    window.requestAnimationFrame(() => {
      this.toggleAttribute('data-open', true);
      this.querySelector('.sleek-subscription-popup__close')?.focus();
    });
  }

  close() {
    if (this.hidden || this.closing) return;
    this.closing = true;
    this.toggleAttribute('data-open', false);
    releaseOwnedBodyLock(this);
    const finish = () => {
      this.hidden = true;
      this.closing = false;
      if (this.returnFocusTo?.isConnected && this.returnFocusTo !== document.body) this.returnFocusTo.focus();
    };
    if (reducedMotion.matches) finish();
    else this.closeTimer = window.setTimeout(finish, MOTION.fast.duration);
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.querySelectorAll('button:not([disabled]), a[href], input:not([disabled])')]
      .filter((node) => !node.hidden && node.tabIndex >= 0 && node.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

class SleekProductDetailDialog extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekDialogReady === 'true') return;
    this.dataset.sleekDialogReady = 'true';
    this.dialog = this.querySelector('dialog');
    this.openButton = this.querySelector('[data-sleek-dialog-open]');
    this.closeButtons = [...this.querySelectorAll('[data-sleek-dialog-close]')];
    this.copyButton = this.querySelector('[data-sleek-product-copy]');
    this.questionForm = this.querySelector('[data-sleek-question-form]');
    this.handleOpen = () => this.open();
    this.handleClose = () => this.close();
    this.handleDialogClose = () => {
      releaseOwnedBodyLock(this);
      this.openButton?.focus();
    };
    this.handleDialogClick = (event) => {
      if (event.target === this.dialog) this.close();
    };
    this.handleCopy = () => this.copy();
    this.handleQuestionSubmit = (event) => this.submitQuestion(event);
    this.openButton?.addEventListener('click', this.handleOpen);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.handleClose));
    this.dialog?.addEventListener('close', this.handleDialogClose);
    this.dialog?.addEventListener('click', this.handleDialogClick);
    this.copyButton?.addEventListener('click', this.handleCopy);
    this.questionForm?.addEventListener('submit', this.handleQuestionSubmit);
  }

  disconnectedCallback() {
    this.openButton?.removeEventListener('click', this.handleOpen);
    this.closeButtons?.forEach((button) => button.removeEventListener('click', this.handleClose));
    this.dialog?.removeEventListener('close', this.handleDialogClose);
    this.dialog?.removeEventListener('click', this.handleDialogClick);
    this.copyButton?.removeEventListener('click', this.handleCopy);
    this.questionForm?.removeEventListener('submit', this.handleQuestionSubmit);
    releaseOwnedBodyLock(this);
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    if (this.dataset.dialogKind === 'share') this.updateShareTargets();
    acquireOwnedBodyLock(this);
    this.dialog.showModal();
    this.dialog.querySelector('[data-sleek-dialog-close]')?.focus();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }

  async copy() {
    const input = this.querySelector('input[readonly]');
    if (!input) return;
    try {
      await navigator.clipboard.writeText(input.value);
      const original = this.copyButton.textContent;
      this.copyButton.textContent = 'Copied';
      window.setTimeout(() => { this.copyButton.textContent = original; }, 1400);
    } catch {
      input.select();
    }
  }

  updateShareTargets() {
    const url = window.location.href;
    const input = this.querySelector('input[readonly]');
    if (input) input.value = url;
    this.querySelectorAll('[data-sleek-share-platform]').forEach((link) => {
      const encoded = encodeURIComponent(url);
      if (link.dataset.sleekSharePlatform === 'facebook') link.href = `https://www.facebook.com/sharer.php?u=${encoded}`;
      if (link.dataset.sleekSharePlatform === 'x') link.href = `https://twitter.com/share?url=${encoded}`;
      if (link.dataset.sleekSharePlatform === 'pinterest') link.href = `https://pinterest.com/pin/create/button/?url=${encoded}`;
    });
  }

  submitQuestion(event) {
    event.preventDefault();
    if (!this.questionForm?.reportValidity()) return;
    const status = this.querySelector('[data-sleek-question-status]');
    if (status) {
      status.hidden = false;
      status.textContent = 'Thanks! We will get back to you soon.';
      status.focus();
    }
    this.questionForm.reset();
  }
}

class SleekProductPairs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sleekPairsReady === 'true') return;
    this.dataset.sleekPairsReady = 'true';
    this.slides = [...this.querySelectorAll('[data-sleek-pairs-slide]')];
    this.fraction = this.querySelector('[data-sleek-pairs-fraction]');
    this.previousButton = this.querySelector('[data-sleek-pairs-prev]');
    this.nextButton = this.querySelector('[data-sleek-pairs-next]');
    this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
    this.handlePrevious = () => this.step(-1);
    this.handleNext = () => this.step(1);
    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.show(this.index);
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
  }

  step(direction) {
    if (this.slides.length < 2) return;
    this.show(normalizeIndex(this.index + direction, this.slides.length));
  }

  show(index) {
    this.index = index;
    this.slides.forEach((slide, slideIndex) => {
      slide.hidden = slideIndex !== index;
      slide.setAttribute('aria-hidden', String(slideIndex !== index));
    });
    if (this.fraction) this.fraction.textContent = `${index + 1} / ${this.slides.length}`;
  }
}

async function refreshCartDrawer() {
  // `/cart` plus an application/json Accept header resolves to Shopify's Ajax
  // Cart API, not the Section Rendering API. Request the section from the
  // locale-aware storefront root so the response contains fresh drawer HTML.
  const sectionUrl = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
  sectionUrl.searchParams.set('sections', 'cart-drawer-section');
  const response = await fetch(sectionUrl, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!response.ok) return false;
  const sections = await response.json();
  const markup = sections['cart-drawer-section'];
  if (!markup) return false;
  const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
  const incoming = documentFragment.querySelector('#shopify-section-cart-drawer-section');
  const current = document.querySelector('#shopify-section-cart-drawer-section');
  if (incoming && current) {
    current.replaceWith(incoming);
    return true;
  }

  // The cart Section Rendering API can return the drawer's content without
  // Shopify's outer section wrapper. Preserve the active drawer element and
  // replace its rendered inner state so an empty drawer cannot remain stale
  // after a successful quick-view or card add.
  const incomingInner = documentFragment.querySelector('#cart-drawer .cart-drawer__inner, .cart-drawer__inner');
  const currentInner = document.querySelector('#cart-drawer .cart-drawer__inner');
  if (!incomingInner || !currentInner) return false;
  currentInner.replaceWith(incomingInner);
  return true;
}

document.addEventListener('click', async (event) => {
  const shareButton = event.target.closest('[data-sleek-share]');
  if (!shareButton) return;
  const shareData = {
    title: shareButton.dataset.shareTitle || document.title,
    url: shareButton.dataset.shareUrl || window.location.href,
  };
  try {
    if (navigator.share) await navigator.share(shareData);
    else {
      await navigator.clipboard.writeText(shareData.url);
      const label = shareButton.textContent;
      shareButton.textContent = 'Copy';
      window.setTimeout(() => { shareButton.textContent = label; }, 1400);
    }
  } catch {
    // Cancelling the native share dialog leaves the article unchanged.
  }
});

function previewVariantMoney(cents) {
  return `$${(Math.max(0, Number(cents) || 0) / 100).toFixed(2)}`;
}

function syncPreviewProductVariant(control) {
  const product = control?.closest?.('[data-preview-product]');
  const variant = control?.matches?.('select') ? control.selectedOptions?.[0] : control;
  const variantId = String(variant?.dataset?.variantId || '');
  if (!product || !variantId) return;

  const available = variant.dataset.variantAvailable === 'true';
  const requiresShipping = variant.dataset.variantRequiresShipping === 'true';
  const optionValue = control.matches('select') ? variant.textContent.trim() : String(control.value || '').trim();
  const price = Math.max(0, Number(variant.dataset.variantPrice) || 0);
  const compareAt = Math.max(0, Number(variant.dataset.variantCompareAt) || 0);

  product.dataset.selectedVariantId = variantId;
  product.dataset.selectedVariantTitle = variant.dataset.variantTitle || optionValue;
  product.dataset.selectedVariantRequiresShipping = String(requiresShipping);

  const selectedValue = product.querySelector('[data-selected-option-value]');
  if (selectedValue) selectedValue.textContent = optionValue;

  const priceNode = product.querySelector('[data-preview-product-price]');
  if (priceNode) priceNode.innerHTML = `${previewVariantMoney(price)}${compareAt > price ? ` <s>${previewVariantMoney(compareAt)}</s>` : ''}`;

  const shipping = product.querySelector('[data-preview-product-shipping]');
  if (shipping) shipping.hidden = !requiresShipping;

  product.querySelectorAll('.sleek-preview-product__option input[data-variant-id]').forEach((input) => {
    input.checked = input.dataset.variantId === variantId;
  });
  product.querySelectorAll('[data-preview-mobile-variant]').forEach((select) => {
    select.value = variantId;
  });
  product.querySelectorAll('[data-preview-add-to-cart], [data-preview-buy-now]').forEach((button) => {
    button.dataset.variantId = variantId;
    button.disabled = !available;
    if (button.hasAttribute('data-preview-add-to-cart')) button.textContent = available ? 'Add To Cart' : 'Sold Out';
  });

  const featuredImage = variant.dataset.variantFeaturedImage;
  if (featuredImage) {
    const galleryItems = [...product.querySelectorAll('[data-preview-gallery-item]')];
    const activeIndex = galleryItems.findIndex((item) => item.dataset.previewGalleryImage === featuredImage);
    if (activeIndex >= 0) {
      galleryItems.forEach((item, index) => item.classList.toggle('is-active', index === activeIndex));
      const status = product.querySelector('[data-preview-gallery-status] span');
      if (status) status.textContent = `${activeIndex + 1} / ${galleryItems.length}`;
    }
  }

  product.dispatchEvent(new CustomEvent('sleek:preview-variant-change', {
    bubbles: true,
    detail: { variantId, optionValue, price, compareAt, available, requiresShipping },
  }));
}

document.addEventListener('change', (event) => {
  const control = event.target?.closest?.('[data-preview-product] input[data-variant-id], [data-preview-product] [data-preview-mobile-variant]');
  if (control) syncPreviewProductVariant(control);
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-preview-buy-now]');
  if (!button || button.dataset.previewBuyNow === undefined) return;
  const quantityInput = button.closest('[data-preview-product]')?.querySelector('.sleek-preview-quantity input');
  const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
  const variantId = Number(button.dataset.variantId);
  const previewEvent = new CustomEvent('sleek:preview-add-items', {
    bubbles: true,
    cancelable: true,
    detail: { items: [{ id: variantId, quantity }], source: 'buy-now' },
  });
  document.dispatchEvent(previewEvent);
  if (previewEvent.defaultPrevented) return;
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
  });
  if (response.ok) {
    await response.json();
    window.location.assign('/checkout');
  }
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-sleek-add-to-cart]');
  if (!button || button.dataset.sleekBusy === 'true') return;
  const variantId = Number(button.dataset.variantId);
  if (!variantId) {
    const productUrl = button.dataset.productUrl;
    const quickView = document.querySelector('#sleek-quick-view');
    if (quickView && productUrl) {
      event.preventDefault();
      quickView?.open(button);
    } else if (productUrl) window.location.assign(productUrl);
    return;
  }
  button.dataset.sleekBusy = 'true';
  button.setAttribute('aria-busy', 'true');
  try {
    const previewEvent = new CustomEvent('sleek:preview-add-items', {
      bubbles: true,
      cancelable: true,
      detail: { items: [{ id: variantId, quantity: 1 }], source: 'product-card' },
    });
    document.dispatchEvent(previewEvent);
    if (previewEvent.defaultPrevented) return;
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    if (!response.ok) throw new Error('Unable to add item');
    await response.json();
    await refreshCartDrawer();
    const drawer = document.querySelector('#cart-drawer');
    if (typeof drawer?.open === 'function') drawer.open();
    else drawer?.querySelector('dialog')?.showModal?.();
  } catch {
    window.location.assign('/cart');
  } finally {
    button.dataset.sleekBusy = 'false';
    button.removeAttribute('aria-busy');
  }
});

if (!customElements.get('sleek-motion')) customElements.define('sleek-motion', SleekMotion);
if (!customElements.get('sleek-tabs')) customElements.define('sleek-tabs', SleekTabs);
if (!customElements.get('sleek-slider')) customElements.define('sleek-slider', SleekSlider);
if (!customElements.get('sleek-accordion-group')) customElements.define('sleek-accordion-group', SleekAccordionGroup);
if (!customElements.get('sleek-grid-toggle')) customElements.define('sleek-grid-toggle', SleekGridToggle);
if (!customElements.get('sleek-collection-catalog')) customElements.define('sleek-collection-catalog', SleekCollectionCatalog);
if (!customElements.get('sleek-clone-slider')) customElements.define('sleek-clone-slider', SleekCloneSlider);
if (!customElements.get('sleek-clone-tabs')) customElements.define('sleek-clone-tabs', SleekCloneTabs);
if (!customElements.get('sleek-clone-drawer')) customElements.define('sleek-clone-drawer', SleekCloneDrawer);
if (!customElements.get('sleek-clone-rail')) customElements.define('sleek-clone-rail', SleekCloneRail);
if (!customElements.get('sleek-products-bundle')) customElements.define('sleek-products-bundle', SleekProductsBundle);
if (!customElements.get('sleek-social-media')) customElements.define('sleek-social-media', SleekSocialMedia);
if (!customElements.get('sleek-clone-accordion')) customElements.define('sleek-clone-accordion', SleekCloneAccordion);
if (!customElements.get('sleek-clone-header')) customElements.define('sleek-clone-header', SleekCloneHeader);
if (!customElements.get('sleek-quick-view')) customElements.define('sleek-quick-view', SleekQuickView);
if (!customElements.get('sleek-page-accordion')) customElements.define('sleek-page-accordion', SleekPageAccordion);
if (!customElements.get('sleek-footer-accordion')) customElements.define('sleek-footer-accordion', SleekFooterAccordion);
if (!customElements.get('sleek-localization-disclosure')) customElements.define('sleek-localization-disclosure', SleekLocalizationDisclosure);
if (!customElements.get('sleek-subscription-popup')) customElements.define('sleek-subscription-popup', SleekSubscriptionPopup);
if (!customElements.get('sleek-product-detail-dialog')) customElements.define('sleek-product-detail-dialog', SleekProductDetailDialog);
if (!customElements.get('sleek-product-pairs')) customElements.define('sleek-product-pairs', SleekProductPairs);

let previousScrollY = window.scrollY;
let scrollFrame = null;
window.addEventListener(
  'scroll',
  () => {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;
      const state = resolveHeaderState(previousScrollY, currentScrollY, 12);
      if (state !== 'idle') document.documentElement.dataset.sleekHeaderState = state;
      previousScrollY = currentScrollY;
      scrollFrame = null;
    });
  },
  { passive: true },
);

document.addEventListener('sleek:lock', () => setBodyLock('acquire'));
document.addEventListener('sleek:unlock', () => setBodyLock('release'));
document.documentElement.dataset.sleekHeaderState = window.scrollY > 12 ? 'pinned' : 'top';
